export type TasteSummary = {
  preferredCuisines: string[];
  avoidedCuisines: string[];
  mixedCuisines: string[];
  preferredFlavors: string[];
  avoidedFlavors: string[];
  mixedFlavors: string[];
  preferredIngredients: string[];
  avoidedIngredients: string[];
  mixedIngredients: string[];
  preferredTechniques: string[];
  avoidedTechniques: string[];
  mixedTechniques: string[];
  preferredFormats: string[];
  avoidedFormats: string[];
  mixedFormats: string[];
  preferredDietary: string[];
  avoidedDietary: string[];
  mixedDietary: string[];
};

export type AxisWeight = {
  posWeight: number;
  negWeight: number;
};

export type TasteProfileData = {
  summary: TasteSummary;
  flavorWeights: Record<string, AxisWeight>;
  cuisineWeights: Record<string, AxisWeight>;
  ingredientWeights: Record<string, AxisWeight>;
  techniqueWeights: Record<string, AxisWeight>;
  formatWeights: Record<string, AxisWeight>;
  textureWeights: Record<string, AxisWeight>;
  totalEvents: number;
  personalityName: string | null;
  personalityBlurb: string | null;
};

// Per-slot taste profiles (users/{uid}/taste docs); a slot appears only once
// its learning passes the backend's chart-worthy floor
export type SlotTasteProfiles = {
  breakfast?: TasteProfileData;
  lunch?: TasteProfileData;
};

// Ingredient category groupings (mirrors backend INGREDIENT_KEYS comments)
export const INGREDIENT_CATEGORIES: Record<string, string[]> = {
  "Proteins": [
    "chicken", "turkey", "beef", "pork", "lamb", "bacon", "sausage",
    "shrimp", "salmon", "tuna", "white_fish", "shellfish", "eggs",
  ],
  "Plant Proteins": [
    "tofu", "tempeh", "seitan", "plant_based_meat", "lentils", "chickpeas",
    "black_beans", "kidney_beans", "white_beans", "beans_generic",
    "peanuts", "cashews", "almonds", "nuts_generic",
  ],
  "Dairy": [
    "cheese_generic", "mozzarella", "cheddar", "parmesan", "feta",
    "goat_cheese", "cream", "butter", "yogurt", "milk", "coconut_milk",
  ],
  "Grains & Carbs": [
    "white_rice", "brown_rice", "rice_noodles", "wheat_noodles", "pasta",
    "bread", "tortilla", "pita", "couscous", "quinoa", "potatoes",
    "sweet_potatoes", "corn",
  ],
  "Vegetables": [
    "mushrooms", "spinach", "kale", "broccoli", "cauliflower", "zucchini",
    "eggplant", "bell_pepper", "cabbage", "carrots", "celery", "cucumber",
    "lettuce", "green_beans", "peas", "tomatoes", "avocado", "asparagus",
  ],
  "Fresh Herbs & Aromatics": [
    "ginger", "lemongrass", "garlic", "onion", "red_onion", "scallions",
    "cilantro", "basil", "mint", "dill", "parsley", "oregano",
    "rosemary", "thyme",
  ],
  "Spices & Seasonings": [
    "chili_peppers", "chili_powder", "jalapeno", "chipotle", "red_pepper_flakes",
    "cumin", "cinnamon", "turmeric", "paprika", "cardamom",
    "star_anise", "fennel_seed", "szechuan_peppercorn", "sumac",
    "fenugreek", "mustard_seed", "italian_seasoning", "sesame_seeds",
  ],
  "Sauces & Condiments": [
    "soy_sauce", "fish_sauce", "oyster_sauce", "mustard", "miso", "curry_paste",
    "curry_powder", "harissa", "gochujang", "hot_sauce", "mayonnaise",
    "honey", "tahini", "peanut_butter", "sesame_oil", "olive_oil",
    "olives", "capers",
  ],
  "Acids": [
    "lemon", "lime", "vinegar_generic", "balsamic_vinegar", "apple_cider_vinegar",
  ],
};

// Flavor axis display labels
export const FLAVOR_LABELS: Record<string, string> = {
  spicy: "Spicy",
  sweet: "Sweet",
  savory: "Savory",
  tangy: "Tangy",
  smoky: "Smoky",
  rich: "Rich",
  fresh: "Fresh",
};

// Ordered list of flavor keys (for consistent radar chart rendering)
export const FLAVOR_KEYS = [
  "spicy", "sweet", "savory", "tangy", "smoky", "rich", "fresh",
] as const;

// Mouthfeel. Deterministically derived from ingredients on the backend rather
// than AI-assigned, so unlike flavor there's no model judgment mixed in.
export const TEXTURE_LABELS: Record<string, string> = {
  crispy: "Crispy",
  creamy: "Creamy",
  saucy: "Saucy",
  chewy: "Chewy",
  crunchy: "Crunchy",
  hearty: "Hearty",
};

export const TEXTURE_KEYS = [
  "crispy", "creamy", "saucy", "chewy", "crunchy", "hearty",
] as const;

// Cuisine display labels
export const CUISINE_LABELS: Record<string, string> = {
  american: "American",
  italian: "Italian",
  mexican: "Mexican",
  chinese: "Chinese",
  japanese: "Japanese",
  korean: "Korean",
  thai: "Thai",
  vietnamese: "Vietnamese",
  indian: "Indian",
  french: "French",
  mediterranean: "Mediterranean",
  "middle eastern": "Middle Eastern",
  latin: "Latin",
  african: "African",
};

// Ordered list of cuisine keys (for consistent strip rendering)
export const CUISINE_KEYS = [
  "american", "italian", "mexican", "chinese", "japanese", "korean",
  "thai", "vietnamese", "indian", "french", "mediterranean",
  "middle eastern", "latin", "african",
] as const;

// Technique display labels
export const TECHNIQUE_LABELS: Record<string, string> = {
  "one-pot": "One-pot",
  "sheet pan": "Sheet pan",
  "slow cooker": "Slow cooker",
  "pressure cooker": "Pressure cooker",
  "air fryer": "Air fryer",
  "fry": "Fry",
  "sauté": "Sauté",
  "grill": "Grill",
  "bake": "Bake",
  "roast": "Roast",
  "boil": "Boil",
  "steam": "Steam",
  "sous vide": "Sous vide",
  "stir-fry": "Stir-fry",
  "no-cook": "No-cook",
};

export const TECHNIQUE_KEYS = [
  "one-pot", "sheet pan", "slow cooker", "pressure cooker", "air fryer",
  "fry", "sauté", "grill", "bake", "roast", "boil", "steam",
  "sous vide", "stir-fry", "no-cook",
] as const;

// Format display labels
export const FORMAT_LABELS: Record<string, string> = {
  bowl: "Bowl",
  salad: "Salad",
  soup_stew: "Soup / stew",
  handheld: "Handheld",
  pasta_noodle: "Pasta / noodle",
  casserole_bake: "Casserole / bake",
  sheet_pan_meal: "Sheet pan meal",
  stir_fry: "Stir-fry",
  main_with_side: "Main with side",
  flatbread_pizza: "Flatbread / pizza",
};

export const FORMAT_KEYS = [
  "bowl", "salad", "soup_stew", "handheld", "pasta_noodle",
  "casserole_bake", "sheet_pan_meal", "stir_fry", "main_with_side",
  "flatbread_pizza",
] as const;

/**
 * Display names the underscore-to-space rule below can't derive, because the
 * key can't encode where a hyphen goes.
 */
export const INGREDIENT_LABEL_OVERRIDES: Record<string, string> = {
  plant_based_meat: "Plant-Based Meat",
};

/**
 * Convert an ingredient key to a readable display name.
 * e.g., "white_rice" -> "White Rice"
 */
export const ingredientDisplayName = (key: string): string =>
  INGREDIENT_LABEL_OVERRIDES[key] ??
  key
    .replace(/_generic$/, "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
